import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { peanudsApi, withAuthenticatedHeaders } from '@peanuds/shared';
import type { GetContextsResponseDtoOutput, GetUserContextsData } from '@peanuds/shared';
import { UserContextLoadError } from './user-context.errors';

type UserOrganization = GetContextsResponseDtoOutput['data']['organizations'][number];

const USER_CONTEXT_LOAD_ATTEMPTS = 3;
const USER_CONTEXT_RETRY_DELAY_MS = 500;

const wait = (delayMs: number) => {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, delayMs);
  });
};

const fetchUserOrganizations = async () => {
  const response = await peanudsApi.getUserContexts<true>(
    await withAuthenticatedHeaders<GetUserContextsData, true>({ throwOnError: true }),
  );

  return response.data.data.organizations;
};

const fetchUserOrganizationsWithRetry = async () => {
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await fetchUserOrganizations();
    } catch (error) {
      if (attempt >= USER_CONTEXT_LOAD_ATTEMPTS) {
        throw new UserContextLoadError(error);
      }

      await wait(USER_CONTEXT_RETRY_DELAY_MS);
    }
  }
};

export const useUserContextStore = defineStore(
  'user-context',
  () => {
    const organizations = ref<UserOrganization[]>([]);
    const selectedOrganizationId = ref<string | null>(null);
    const isLoaded = ref(false);
    const isLoading = ref(false);
    const loadError = ref<unknown>(null);

    let loadPromise: Promise<UserOrganization[]> | null = null;
    let refreshPromise: Promise<UserOrganization[]> | null = null;
    let refreshAutoSelectSingleOrganization = true;
    let loadGeneration = 0;

    const resolvedOrganizationId = computed(() => {
      if (!isLoaded.value) {
        return null;
      }

      if (selectedOrganizationId.value) {
        return selectedOrganizationId.value;
      }

      if (organizations.value.length === 1) {
        return organizations.value[0]?.id ?? null;
      }

      return null;
    });

    const selectedOrganization = computed(() => {
      if (!selectedOrganizationId.value) {
        return null;
      }

      return organizations.value.find(({ id }) => id === selectedOrganizationId.value) ?? null;
    });

    const hasOrganizations = computed(() => organizations.value.length > 0);
    const requiresOrganizationCreation = computed(() => {
      return isLoaded.value && organizations.value.length === 0;
    });
    const requiresOrganizationSelection = computed(() => {
      return isLoaded.value && organizations.value.length > 0 && !selectedOrganization.value;
    });

    const reconcileSelection = (autoSelectSingleOrganization = true) => {
      if (organizations.value.length === 1) {
        const organizationId = organizations.value[0]?.id ?? null;

        if (autoSelectSingleOrganization) {
          selectedOrganizationId.value = organizationId;
          return;
        }

        if (selectedOrganizationId.value === organizationId) {
          return;
        }

        selectedOrganizationId.value = null;
        return;
      }

      if (
        selectedOrganizationId.value &&
        organizations.value.some(({ id }) => id === selectedOrganizationId.value)
      ) {
        return;
      }

      selectedOrganizationId.value = null;
    };

    const loadUserContexts = async (options?: {
      force?: boolean;
      autoSelectSingleOrganization?: boolean;
    }): Promise<UserOrganization[]> => {
      const force = options?.force ?? false;
      const autoSelectSingleOrganization = options?.autoSelectSingleOrganization ?? true;

      if (!force && isLoaded.value) {
        return organizations.value;
      }

      if (loadPromise) {
        if (!force) return loadPromise;

        refreshAutoSelectSingleOrganization =
          refreshAutoSelectSingleOrganization && autoSelectSingleOrganization;
        if (!refreshPromise) {
          const generation = loadGeneration;
          refreshPromise = loadPromise
            // A failed request must not discard a later invalidation.
            .catch(() => undefined)
            .then(() => {
              if (generation !== loadGeneration) return organizations.value;
              const refreshOptions = {
                force: true,
                autoSelectSingleOrganization: refreshAutoSelectSingleOrganization,
              };
              refreshAutoSelectSingleOrganization = true;
              refreshPromise = null;
              return loadUserContexts(refreshOptions);
            });
        }

        return refreshPromise;
      }

      if (!force && loadError.value) {
        throw loadError.value;
      }

      isLoading.value = true;
      loadError.value = null;
      const generation = loadGeneration;

      loadPromise = (async () => {
        try {
          const loadedOrganizations = await fetchUserOrganizationsWithRetry();
          if (generation !== loadGeneration) return organizations.value;

          organizations.value = loadedOrganizations;
          reconcileSelection(autoSelectSingleOrganization);
          isLoaded.value = true;

          return organizations.value;
        } catch (error) {
          if (generation === loadGeneration) {
            loadError.value = error;
            isLoaded.value = false;
          }
          throw error;
        } finally {
          if (generation === loadGeneration) {
            isLoading.value = refreshPromise !== null;
            loadPromise = null;
          }
        }
      })();

      return loadPromise;
    };

    const ensureLoaded = loadUserContexts;

    const selectOrganization = (organizationId: string | null) => {
      if (!organizationId) {
        selectedOrganizationId.value = null;
        return;
      }

      const organizationExists = organizations.value.some(({ id }) => id === organizationId);

      if (!organizationExists) {
        throw new Error(`Unknown organization selected: ${organizationId}`);
      }

      selectedOrganizationId.value = organizationId;
    };

    const syncSelectedOrganizationFromToken = (organizationId: string | null) => {
      if (!organizationId) {
        selectedOrganizationId.value = null;
        return;
      }

      selectedOrganizationId.value = organizationId;
    };

    const clearLoadError = () => {
      loadError.value = null;
    };

    const reset = () => {
      loadGeneration += 1;
      organizations.value = [];
      selectedOrganizationId.value = null;
      isLoaded.value = false;
      isLoading.value = false;
      loadError.value = null;
      loadPromise = null;
      refreshPromise = null;
      refreshAutoSelectSingleOrganization = true;
    };

    return {
      organizations,
      selectedOrganizationId,
      resolvedOrganizationId,
      selectedOrganization,
      isLoaded,
      isLoading,
      loadError,
      hasOrganizations,
      requiresOrganizationCreation,
      requiresOrganizationSelection,
      loadUserContexts,
      ensureLoaded,
      selectOrganization,
      syncSelectedOrganizationFromToken,
      clearLoadError,
      reset,
    };
  },
  {
    persist: {
      pick: ['selectedOrganizationId'],
    },
  },
);
